// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { RootState } from "../../../app/store"
import { prepareListDaySummaryRequest } from "./daySummaries"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import {
  ClearTimeGroupErrorsRequest,
  ClearTimeGroupErrorsResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type ClearTimeGroupsErrorRpc = (request: ClearTimeGroupErrorsRequest) => Observable<ClearTimeGroupErrorsResponse>

export const clearTimeGroupErrors = (clearTimeGroupsErrorRpc: ClearTimeGroupsErrorRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.clearTimeGroupErrors.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => of(action.payload)
        .pipe(
          mergeMap(clearTimeGroupsErrorRpc),
          mergeMap(_ => concat(
            of(TimelineSlice.clearTimeGroupsPostErrors(action.payload.getTimeGroupIdsList())),
            of(Actions.listDaySummaries(prepareListDaySummaryRequest(state.timeline.activeDate.toString())))
          )),
          catchError(handleError),
        )
      ),
    )

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_clear_time_rows_error_failed_precondition", message: "Failed to clear errors. Time rows may be out of sync." }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_clear_time_rows_error_permission_denied", message: "Failed to clear errors. You do not have permission to group the selected time rows." }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_clear_time_rows_error_failed", message: "Failed to clear errors. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}
const clearTimeGroupsErrorRpc = (request: ClearTimeGroupErrorsRequest): Observable<ClearTimeGroupErrorsResponse> =>
  grpc(() => timelineClient.clearTimeGroupErrors(request, null))

export default clearTimeGroupErrors(clearTimeGroupsErrorRpc)
